import type { SimulationNode } from '../models/simulation';
import { extractVariableNames, buildNameToIdMap, replaceVariableReferences } from './expressionParser';
import { inferResultUnit, normalizeUnit, parseUnitFromName } from './units';

/**
 * Derives the resulting unit for a formula node based on its inputs.
 * 
 * @param formulaNode - The formula node
 * @param allNodes - All nodes in the graph
 * @returns The derived unit string, or undefined if no unit can be derived
 */
export const deriveFormulaUnit = (
    formulaNode: SimulationNode,
    allNodes: SimulationNode[]
): string | undefined => {
    if (formulaNode.data.type !== 'formula') {
        return undefined;
    }

    try {
        const expression = formulaNode.data.expression;
        const variableNames = extractVariableNames(expression);
        const nameToIdMap = buildNameToIdMap(allNodes);

        const { expression: sanitizedExpression, identifierMap } = replaceVariableReferences(
            expression,
            variableNames
        );

        // Build a scope with units from referenced nodes
        const scope: Record<string, string | undefined> = {};

        identifierMap.forEach((originalName, identifier) => {
            const nodeId = nameToIdMap.get(originalName);
            if (!nodeId) {
                return;
            }

            const node = allNodes.find((n) => n.id === nodeId);
            if (!node) {
                return;
            }

            if (node.data.type === 'distribution') {
                scope[identifier] = node.data.unit ?? parseUnitFromName(node.data.name) ?? undefined;
            } else if (node.data.type === 'formula') {
                // Recursively derive units for formula dependencies
                scope[identifier] = deriveFormulaUnit(node, allNodes) ?? parseUnitFromName(node.data.name) ?? undefined;
            }
        });

        const derivedUnit = inferResultUnit(sanitizedExpression, scope);
        // Normalize the derived unit to ensure consistent formatting
        return normalizeUnit(derivedUnit ?? parseUnitFromName(formulaNode.data.name) ?? undefined);
    } catch (error) {
        console.warn('Error deriving formula unit:', error);
        return undefined;
    }
};

